import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageSquare, ArrowRight, Wind, CloudHail, TreePine, ShieldCheck, Award, Clock, FileCheck, Star, MapPin, Target, BadgeCheck, SearchCheck, Home as HomeIcon, Handshake, CheckCircle2 } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { JdBio } from "@/components/site/JdBio";
import { TestimonialsGallery } from "@/components/site/TestimonialsGallery";
import { InspectionForm } from "@/components/site/InspectionForm";
import { InspectionModal } from "@/components/site/InspectionModal";
import { GeorgiaMap } from "@/components/site/GeorgiaMap";
import { AwardBadge } from "@/components/site/AwardBadge";
import { OCPlatinumBadge, GAFNationalCircleBadge, NationsLargestBadge } from "@/components/site/TrustBadges";
import { COUNTIES, PHONE, PHONE_TEL, TRUST_BADGES, SHINGLE_LINES, GOOGLE_REVIEW_URL } from "@/lib/constants";

const HERO_BG = "/storm-bg.png";

export default function Home() {
  const [autoInspectOpen, setAutoInspectOpen] = useState(false);

  useEffect(() => {
    if (window.location.hash === "#inspect") {
      document.getElementById("inspect")?.scrollIntoView({ behavior: "smooth" });
    }
    // Auto-open the Free Inspection modal on first paint of the landing page
    const t = setTimeout(() => setAutoInspectOpen(true), 600);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      {/* Auto-opening Free Inspection modal — visible immediately on landing */}
      {autoInspectOpen && <InspectionModal defaultOpen />}

      <Header />

      {/* URGENCY MARQUEE — top of page */}
      <section
        data-testid="urgency-bar"
        className="overflow-hidden border-y-2 border-red-600 text-white"
        style={{
          background:
            "linear-gradient(90deg, #020617 0%, #0c1a3d 22%, #1e3a8a 50%, #0c1a3d 78%, #020617 100%)",
        }}
      >
        <div className="marquee-track py-2.5">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-10 px-5 font-impact uppercase text-base sm:text-lg tracking-wider whitespace-nowrap">
              {[
                "Free Roof Inspections",
                "Most Replacements in 1 Day",
                "Pay Only Your Deductible",
                "Top 100 Roofing Contractor",
                "Call JD · 470-790-7744",
                "Storm Damage Specialists",
                "A+ BBB Rated",
                "Thousands of 5-Star Google Reviews",
              ].map((t, i) => (
                <span key={`${k}-${i}`} className="flex items-center gap-3">
                  <span className="text-red-500">⚡</span>
                  {t}
                  <span className="text-red-500 text-2xl">•</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* TRUST BAR — Certified contractor badges (top of page) */}
      <section data-testid="trust-bar" className="bg-gradient-to-r from-slate-950 via-blue-950/50 to-slate-950 border-b-2 border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-1.5 md:py-2">
          <div className="flex items-center justify-center gap-3 md:gap-6 relative">
            {/* Glow ring behind badges */}
            <div
              aria-hidden
              className="absolute inset-0 -my-1 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse at center, rgba(125,211,252,0.22) 0%, rgba(125,211,252,0) 70%)",
              }}
            />
            <OCPlatinumBadge className="relative max-h-[56px] opacity-90 hover:opacity-100 hover:scale-105 transition" />
            <GAFNationalCircleBadge className="relative max-h-[96px] drop-shadow-[0_0_18px_rgba(125,211,252,0.45)] hover:scale-110 transition-transform" />
            <NationsLargestBadge className="relative max-h-[56px] opacity-90 hover:opacity-100 hover:scale-105 transition" />
          </div>
        </div>
      </section>

      {/* HERO */}
      <section data-testid="hero" className="relative overflow-hidden border-b border-slate-900 min-h-[92vh] flex items-center">
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-slate-950/55 to-slate-950" />
        <div className="absolute inset-0 grain" />

        <div className="relative w-full max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 fade-up">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            {/* LEFT — Title + extra */}
            <div className="lg:col-span-7 text-center lg:text-left">
              {/* Top eyebrow with red rules */}
              <div className="flex items-center justify-center lg:justify-start gap-4 mb-6">
                <span className="hidden sm:block h-px w-12 bg-red-600" />
                <div className="text-[11px] sm:text-sm uppercase tracking-[0.35em] text-white font-bold">
                  Wind <span className="text-red-600">·</span> Hail <span className="text-red-600">·</span> Tree Damage Specialist
                </div>
                <span className="hidden sm:block h-px w-12 bg-red-600" />
              </div>

              {/* Massive headline */}
              <h1 className="font-impact uppercase leading-[0.92] text-white text-6xl sm:text-7xl lg:text-[6.5rem] tracking-tight drop-shadow-[0_8px_24px_rgba(0,0,0,0.6)]">
                <span className="text-grunge">Storm</span>
                <br />
                <span className="text-grunge">Damage</span>
                <span className="text-white">?</span>
              </h1>

              {/* "We Can Help!" red brush stroke */}
              <div className="mt-3 inline-block">
                <span
                  className="font-impact uppercase italic text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none"
                  style={{
                    background: "linear-gradient(180deg, #ffffff 0%, #ef4444 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    textShadow: "0 6px 18px rgba(220,38,38,0.4)",
                  }}
                >
                  We Can Help!
                </span>
                <div className="h-1 w-32 bg-red-600 mt-1" />
              </div>

              {/* Body copy */}
              <div className="mt-8 space-y-3 max-w-2xl mx-auto lg:mx-0">
                <p className="text-slate-200 text-base sm:text-lg">
                  One missing or damaged shingle seen from the ground
                </p>
                <p className="font-impact uppercase text-white text-xl sm:text-2xl lg:text-3xl tracking-tight leading-tight">
                  May qualify for a full <span className="text-red-600">roof replacement</span>
                  <br className="hidden sm:block" />
                  <span className="sm:hidden"> </span>through your homeowner's insurance.
                </p>
                <p className="text-slate-300 text-sm sm:text-base pt-2">
                  JD verifies claim eligibility — <span className="text-red-500">at no cost to you.</span>
                </p>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center lg:justify-start items-stretch sm:items-center">
                <a
                  data-testid="hero-call"
                  href={`tel:${PHONE_TEL}`}
                  className="bg-slate-950/70 backdrop-blur border-2 border-slate-700 text-white font-bold uppercase tracking-widest hover:border-red-600 hover:text-red-500 px-6 py-3 inline-flex items-center justify-center gap-2 transition-colors text-xs"
                >
                  <Phone size={14} className="text-red-600" /> Call JD · {PHONE}
                </a>
                <a
                  data-testid="hero-text"
                  href={`sms:${PHONE_TEL}`}
                  className="bg-slate-950/70 backdrop-blur border-2 border-slate-700 text-white font-bold uppercase tracking-widest hover:border-red-600 hover:text-red-500 px-6 py-3 inline-flex items-center justify-center gap-2 transition-colors text-xs"
                >
                  <MessageSquare size={14} className="text-red-600" /> Text JD
                </a>
              </div>

              {/* Stat strip */}
              <div className="mt-10 bg-slate-950/80 backdrop-blur border border-slate-800 grid grid-cols-2 md:grid-cols-4">
                {[
                  { Icon: ShieldCheck, k: "$0", v: "Inspection Cost" },
                  { Icon: BadgeCheck, k: "16+", v: "Years Experience" },
                  { Icon: MapPin, k: String(COUNTIES.length), v: "GA Counties" },
                  { Icon: Target, k: "24h", v: "Response" },
                ].map(({ Icon, k, v }, i) => (
                  <div
                    key={v}
                    className={`p-4 flex items-center gap-3 border-slate-800 ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "border-b md:border-b-0" : ""} ${i === 1 ? "md:border-r" : ""}`}
                  >
                    <Icon className="text-red-600 shrink-0" size={28} strokeWidth={1.5} />
                    <div className="text-left">
                      <div className="font-impact text-white text-2xl leading-none">{k}</div>
                      <div className="text-[9px] uppercase tracking-[0.2em] text-red-500 font-bold mt-1">{v}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Deductible note — tied to $0 Inspection Cost */}
              <div
                data-testid="inspection-cost-note"
                className="mt-3 relative bg-slate-950/85 backdrop-blur border-l-4 border-red-600 px-4 py-3 sm:px-5 sm:py-4 text-left"
              >
                <div className="text-[10px] uppercase tracking-[0.25em] text-red-500 font-bold mb-1">
                  About the $0 inspection
                </div>
                <p className="text-slate-200 text-sm sm:text-base leading-relaxed">
                  The inspection is <span className="text-red-500 font-bold">100% free</span>. If
                  insurance approves a claim, your <span className="text-white font-bold">deductible</span>{" "}
                  is generally all you have to pay — JD walks you through everything on site.{" "}
                  <span className="text-slate-300">
                    Have your insurance policy handy, or email it ahead to{" "}
                    <a
                      href="mailto:jd.dean@bestchoiceroofing.com"
                      data-testid="email-policy-link"
                      className="text-red-500 hover:text-red-400 underline decoration-red-600/40 underline-offset-4 break-all font-semibold"
                    >
                      jd.dean@bestchoiceroofing.com
                    </a>{" "}
                    for better details.
                  </span>
                </p>
              </div>
            </div>

            {/* RIGHT — Inspection form */}
            <div id="inspect" data-testid="inspect-section" className="lg:col-span-5 scroll-mt-24">
              <InspectionForm />
            </div>
          </div>
        </div>
      </section>

      {/* 2025 AWARDS */}
      <section data-testid="awards" className="bg-slate-950 border-b border-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
          <div className="text-xs uppercase tracking-[0.3em] text-red-600 mb-2 text-center">
            2025 Recognition
          </div>
          <h2 className="font-display font-black uppercase text-2xl sm:text-3xl text-white text-center tracking-tight mb-10">
            Recently Awarded
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10 items-center justify-items-center max-w-3xl mx-auto">
            <AwardBadge variant="volume" />
            <AwardBadge variant="mvp" />
            <AwardBadge variant="warranty" />
            <div className="flex flex-col items-center gap-2">
              <div className="font-display font-black text-red-600 text-xs uppercase tracking-[0.3em]">
                2026
              </div>
              <GAFNationalCircleBadge className="max-h-[170px]" />
            </div>
          </div>
        </div>
      </section>

      {/* INSURABLE DAMAGE BANNER */}
      <section data-testid="insurable-banner" className="bg-gradient-to-r from-slate-950 via-blue-950 to-slate-950 border-y-2 border-sky-500/40">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14 grid md:grid-cols-[auto_1fr] gap-8 items-center">
          <div className="shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-full bg-red-600 flex items-center justify-center shadow-[0_0_40px_rgba(220,38,38,0.55)] border-2 border-white">
            <ShieldCheck className="text-white" size={48} strokeWidth={2.2} />
          </div>
          <div>
            <div className="font-impact uppercase text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-none">
              Storm damage is{" "}
              <span className="text-sky-300">Insurable Damage.</span>
            </div>
            <p className="text-slate-200 text-base sm:text-lg mt-4 max-w-3xl leading-relaxed">
              Your insurance can cover the full cost — <span className="text-sky-300 font-bold">you pay only your deductible and upgrades.</span>
            </p>
          </div>
        </div>
      </section>

      {/* TRUST PILLARS — FREE / EXPERIENCED / TRUSTED */}
      <section data-testid="trust-pillars" className="bg-slate-950 border-b border-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {[
              { Icon: SearchCheck, top: "Free", bottom: "Roof Inspections" },
              { Icon: HomeIcon, top: "Experienced", bottom: "Local & Nationwide" },
              { Icon: Handshake, top: "Trusted", bottom: "Reliable. Local." },
            ].map(({ Icon, top, bottom }) => (
              <div key={top} className="flex items-center gap-5">
                <div className="shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-full bg-blue-700 flex items-center justify-center shadow-[0_0_30px_rgba(29,78,216,0.5)] border border-blue-400/40">
                  <Icon className="text-white" size={32} strokeWidth={2} />
                </div>
                <div>
                  <div className="font-impact uppercase text-white text-3xl sm:text-4xl leading-none">{top}</div>
                  <div className="font-impact uppercase text-sky-300 text-lg sm:text-xl leading-tight mt-1">
                    {bottom}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHEN YOU WANT THE BEST */}
      <section data-testid="the-best" className="relative bg-gradient-to-b from-slate-950 via-blue-950 to-slate-950 border-b border-slate-900 overflow-hidden">
        {/* Sunburst rays */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(125,211,252,0.45) 0%, rgba(125,211,252,0) 60%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <div className="font-impact uppercase text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
            When you want
          </div>
          <div
            className="font-impact uppercase italic text-6xl sm:text-7xl lg:text-[7rem] tracking-tight leading-none mt-2"
            style={{
              background: "linear-gradient(180deg, #ffffff 0%, #93c5fd 60%, #1e40af 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              textShadow: "0 8px 30px rgba(30,64,175,0.45)",
            }}
          >
            The Best,
          </div>
          <div className="font-impact uppercase text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mt-4">
            You Hire <span className="text-sky-300">The Best.</span>
          </div>
          <div className="h-1 w-40 bg-sky-400 mx-auto mt-8" />

          {/* National network stats */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-5 gap-6">
            {[
              { k: "150,000+", v: "Projects Completed" },
              { k: "1 Billion+", v: "Shingles Installed" },
              { k: "5-Star", v: "Google Reviews" },
              { k: "85+", v: "Locations Nationwide" },
              { k: "16+", v: "Years in Business" },
            ].map((s) => (
              <div key={s.v} className="text-center">
                <div className="font-impact uppercase text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-none">
                  {s.k}
                </div>
                <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-sky-300 font-bold mt-2">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
          <p className="text-slate-400 text-xs mt-6 max-w-2xl mx-auto">
            Combined track record of JD&apos;s national roofing network.
          </p>
        </div>
      </section>

      {/* WHY JD — AD STYLE */}
      <section data-testid="why-jd" className="relative bg-gradient-to-b from-slate-950 via-blue-950 to-slate-950 border-y-2 border-sky-400/30 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-25 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at top, rgba(125,211,252,0.4) 0%, rgba(125,211,252,0) 60%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24">
          {/* Headline */}
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-[0.4em] text-sky-300 font-bold mb-3">
              Why Homeowners Pick Us
            </div>
            <h2 className="font-impact uppercase text-white text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95]">
              The Numbers Don't Lie.
            </h2>
            <div className="h-1 w-40 bg-sky-400 mx-auto mt-6" />
            <p className="text-slate-200 text-lg sm:text-xl mt-6 max-w-2xl mx-auto">
              <span className="font-bold text-white">17 years.</span>{" "}
              <span className="text-sky-300">150,000+ homeowners.</span>{" "}
              <span className="font-bold text-white">1 billion shingles.</span>{" "}
              Nobody does it like us.
            </p>
          </div>

          {/* 4 huge stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-14">
            {[
              { big: "17", sup: "YRS", label: "In Business · Since 2009" },
              { big: "85+", label: "Locations Nationwide · 24+ States" },
              { big: "150K+", label: "Homeowners Served" },
              { big: "1B+", label: "Shingles Installed" },
            ].map((s) => (
              <div
                key={s.label}
                className="bg-slate-950/70 border-2 border-sky-400/50 hover:border-sky-400 backdrop-blur p-5 md:p-6 text-center transition-colors"
              >
                <div className="font-impact uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight">
                  {s.big}
                  {s.sup && (
                    <span className="text-sky-300 text-2xl sm:text-3xl ml-1 align-top">
                      {s.sup}
                    </span>
                  )}
                </div>
                <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-sky-300 font-bold mt-3 leading-tight">
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          {/* Credentials row */}
          <div className="text-center mb-8">
            <div className="font-impact uppercase text-white text-3xl sm:text-4xl tracking-tight">
              <span className="text-sky-300">Best-in-Class</span> Credentials
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { t: "GAF National Circle™ Contractor", s: "Exclusive top-performer recognition" },
              { t: "Largest Owens Corning Platinum Preferred Contractor in the Nation", s: "" },
              { t: "Top 100 Roofing Contractor", s: "Industry-ranked" },
              { t: "A+ BBB Rating", s: "Accredited business" },
              { t: "Thousands of 5-Star Google Reviews", s: "Real homeowner stories" },
              { t: "One of the Largest Residential Roofers in the U.S.", s: "" },
            ].map((c) => (
              <div
                key={c.t}
                className="bg-sky-400/5 border-l-4 border-sky-400 px-5 py-4 hover:bg-sky-400/10 transition-colors"
              >
                <div className="font-impact uppercase text-white text-lg sm:text-xl leading-tight tracking-tight">
                  {c.t}
                </div>
                {c.s && (
                  <div className="text-sky-200/70 text-xs uppercase tracking-wide mt-1.5">
                    {c.s}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Bottom punchline */}
          <div className="mt-16 text-center">
            <div className="font-impact uppercase text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
              Storm Damage Restoration.{" "}
              <span className="text-sky-300">Insurance Claims.</span>{" "}
              Free Inspections.
            </div>
            <p className="text-slate-300 text-base sm:text-lg mt-4 max-w-2xl mx-auto">
              Most roof replacements completed in <span className="text-sky-300 font-bold">as little as one day.</span>
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES — AD STYLE */}
      <section className="py-20 md:py-28 bg-slate-950 border-b border-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-[0.4em] text-sky-300 font-bold mb-3">
              What JD Handles
            </div>
            <h2 className="font-impact uppercase text-white text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95]">
              Storm Damage <span className="text-sky-300">Your Insurance</span><br />
              Should Cover.
            </h2>
            <div className="h-1 w-40 bg-sky-400 mx-auto mt-6" />
            <p className="text-slate-300 text-lg mt-6 max-w-2xl mx-auto">
              Most homeowners don't realize their roof already qualifies. JD knows exactly what
              adjusters look for — and what they miss.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {[
              { Icon: Wind, n: "01", t: "Wind Damage", d: "Lifted, creased, or missing shingles from straight-line winds or storms." },
              { Icon: CloudHail, n: "02", t: "Hail Damage", d: "Granule loss, bruising, and fractured mats from impact events." },
              { Icon: TreePine, n: "03", t: "Tree / Limb Damage", d: "Punctures, impact damage, and structural concerns from fallen limbs." },
              { Icon: FileCheck, n: "04", t: "Insurance Claims", d: "JD walks the roof with your adjuster and documents the damage properly." },
            ].map(({ Icon, n, t, d }) => (
              <div
                key={t}
                className="group relative bg-slate-900/60 border-2 border-slate-800 hover:border-sky-400 p-6 md:p-7 transition-all hover:-translate-y-1"
              >
                <div className="flex items-start justify-between mb-4">
                  <Icon className="text-sky-300" size={36} strokeWidth={1.7} />
                  <span className="font-impact text-slate-800 group-hover:text-sky-300/40 text-5xl leading-none transition-colors">
                    {n}
                  </span>
                </div>
                <h3 className="font-impact uppercase text-white text-2xl tracking-tight">{t}</h3>
                <div className="h-0.5 w-10 bg-sky-400 my-3" />
                <p className="text-slate-300 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="how-it-works" className="py-24 md:py-32 border-y border-slate-900 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-red-600 mb-4">How it works</div>
          <h2 className="font-display font-black uppercase text-4xl sm:text-5xl text-white leading-none tracking-tight mb-16 max-w-3xl">
            Three steps to a new roof — at little or no out-of-pocket cost.
          </h2>
          <div className="grid md:grid-cols-3 gap-px bg-slate-800 border border-slate-800">
            {[
              { n: "01", Icon: Clock, t: "Schedule", d: "Call, text, or send the form. JD picks a time that works for you." },
              { n: "02", Icon: ShieldCheck, t: "Free inspection", d: "JD inspects the roof, takes photos, and documents storm damage." },
              { n: "03", Icon: Award, t: "Claim & install", d: "JD walks the roof with your adjuster and crews handle the replacement." },
            ].map(({ n, Icon, t, d }) => (
              <div key={n} className="bg-slate-950 p-10">
                <div className="flex items-baseline justify-between">
                  <span className="font-display font-black text-6xl text-slate-800">{n}</span>
                  <Icon className="text-red-600" size={28} />
                </div>
                <h3 className="font-display font-bold uppercase text-2xl text-white mt-6">{t}</h3>
                <p className="text-slate-400 mt-3 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSPECTION FORM moved up — original location now empty */}

      {/* SERVICE AREAS */}
      <section data-testid="service-area" className="py-24 md:py-32 border-t border-slate-900 bg-slate-900/40 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-red-600 mb-4 text-center">
            Where JD works
          </div>
          <h2 className="font-display font-black uppercase text-4xl sm:text-5xl text-white leading-none tracking-tight text-center">
            {COUNTIES.length} Georgia counties served
          </h2>
          <p className="text-slate-400 mt-5 text-center max-w-2xl mx-auto">
            Highlighted counties below are part of JD's regular inspection route across metro Atlanta
            and west/south Georgia.
          </p>

          <div className="mt-12 grid lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-3">
              <GeorgiaMap />
              <div className="mt-6 flex items-center justify-center gap-6 text-xs uppercase tracking-widest">
                <span className="inline-flex items-center gap-2 text-slate-300">
                  <span className="inline-block w-4 h-4 bg-red-600 border border-red-300" /> Serviced
                </span>
                <span className="inline-flex items-center gap-2 text-slate-500">
                  <span className="inline-block w-4 h-4 bg-slate-800 border border-slate-600" /> Not in route
                </span>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="text-xs uppercase tracking-[0.3em] text-red-600 mb-4">
                Counties on JD's route
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {[...COUNTIES].sort().map((c) => (
                  <div
                    key={c}
                    className="font-display font-bold uppercase text-base text-white border-l-2 border-red-600 pl-3 py-1"
                  >
                    {c}
                  </div>
                ))}
              </div>
              <p className="text-slate-500 text-sm mt-6">
                Not in one of these counties? Call or text JD — he may still be able to help.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SHINGLE TEASER */}
      <section data-testid="shingle-teaser" className="py-24 md:py-32 bg-slate-950 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-red-600 mb-4">
              Premium GAF shingles
            </div>
            <h2 className="font-display font-black uppercase text-4xl sm:text-5xl text-white leading-none tracking-tight">
              See the shingles you'll get to choose from.
            </h2>
            <p className="text-slate-400 mt-6 leading-relaxed text-lg max-w-md">
              GAF Timberline Natural Shadow, HDZ, and UHDZ lines — with industry-leading wind ratings
              and limited lifetime warranties.
            </p>
            <Link
              data-testid="see-shingles"
              to="/shingles"
              className="mt-8 inline-flex items-center gap-2 bg-red-600 text-white font-bold uppercase tracking-wide hover:bg-red-500 px-8 py-4 transition-colors"
            >
              See shingle samples <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {SHINGLE_LINES.map((s, i) => (
              <div key={i} className="aspect-[3/4] bg-slate-900 overflow-hidden border border-slate-800">
                <img src={s.image} alt={s.alt || s.name} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEW CTA */}
      <section data-testid="review-cta" className="py-20 md:py-24 border-t border-slate-900 bg-slate-950">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <a
            data-testid="google-reviews-badge"
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-white rounded-md px-4 py-2 mb-6 hover:scale-105 transition-transform shadow-[0_0_24px_rgba(125,211,252,0.5)] ring-2 ring-sky-400/60"
            title="Read our Google reviews"
          >
            <img
              src="/google-reviews.png"
              alt="Google · Thousands of 5-Star Reviews"
              className="h-12 w-auto object-contain"
            />
          </a>
          <div className="flex items-center justify-center gap-1 mb-5 text-red-600">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} size={22} fill="currentColor" />
            ))}
          </div>
          <div className="text-xs uppercase tracking-[0.3em] text-red-600 mb-3">
            Worked with JD before?
          </div>
          <h2 className="font-display font-black uppercase text-4xl sm:text-5xl text-white tracking-tight">
            Leave a Google review.
          </h2>
          <p className="text-slate-400 mt-5 max-w-xl mx-auto leading-relaxed">
            Your review helps other homeowners trust JD with their storm damage claim — it takes
            less than 60 seconds.
          </p>
          <a
            data-testid="google-review-btn"
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 bg-red-600 text-white font-bold uppercase tracking-wide hover:bg-red-500 px-8 py-4 transition-colors"
          >
            <Star size={16} fill="currentColor" /> Leave a Google review
          </a>
        </div>
      </section>

      {/* FINAL CTA — Schedule your free inspection today */}
      <section
        data-testid="final-cta"
        className="relative overflow-hidden border-y-4 border-red-600"
        style={{
          background:
            "linear-gradient(90deg, #7f1d1d 0%, #b91c1c 18%, #0c1a3d 50%, #b91c1c 82%, #7f1d1d 100%)",
        }}
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 20% 50%, rgba(125,211,252,0.35) 0%, transparent 60%), radial-gradient(circle at 85% 50%, rgba(220,38,38,0.5) 0%, transparent 60%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-20 grid md:grid-cols-[1fr_auto] gap-10 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.4em] text-sky-300 font-bold mb-3">
              Limited Calendar · Book Today
            </div>
            <h2 className="font-impact uppercase text-white text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] drop-shadow-[0_4px_24px_rgba(0,0,0,0.55)]">
              Schedule Your<br />
              <span className="text-sky-300">FREE Inspection</span> Today!
            </h2>
            <p className="text-slate-100 text-lg sm:text-xl mt-5 max-w-2xl font-semibold">
              Don't wait — most claims get denied because homeowners file too late.
              JD verifies eligibility on the spot. No cost. No pressure.
            </p>
          </div>
          <div className="flex flex-col gap-3 shrink-0">
            <a
              data-testid="final-cta-call"
              href={`tel:${PHONE_TEL}`}
              className="bg-slate-950 text-white font-impact uppercase tracking-wide hover:bg-slate-800 px-8 py-5 inline-flex items-center justify-center gap-3 text-2xl transition-colors shadow-lg border-2 border-white"
            >
              <Phone size={22} className="text-red-500" /> {PHONE}
            </a>
            <a
              data-testid="final-cta-text"
              href={`sms:${PHONE_TEL}`}
              className="bg-red-600 text-white font-impact uppercase tracking-wide hover:bg-red-500 px-8 py-5 inline-flex items-center justify-center gap-3 text-2xl transition-colors shadow-lg border-2 border-white"
            >
              <MessageSquare size={22} /> Text JD Now
            </a>
            <a
              data-testid="final-cta-book"
              href="#inspect"
              className="bg-white text-slate-950 font-bold uppercase tracking-wide hover:bg-slate-100 px-8 py-3 inline-flex items-center justify-center gap-2 text-sm transition-colors"
            >
              Book Online <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      <TestimonialsGallery />

      <JdBio />

      <Footer />
    </>
  );
}
